"use client"

import * as React from "react"
import { ThemeProvider as NextThemesProvider } from "next-themes"

// ============================================================================
// TypeScript Types
// ============================================================================

/**
 * Props for the ThemeProvider component
 */
export interface ThemeProviderProps {
  /** Child components to wrap with theme context */
  children: React.ReactNode
  /** Additional props to pass to next-themes ThemeProvider */
  [key: string]: unknown
}

// ============================================================================
// Component
// ============================================================================

/**
 * ThemeProvider Component
 *
 * Wraps the application with next-themes ThemeProvider to enable dark mode support.
 * Configures theme system to use CSS class attribute and respect system preferences.
 *
 * Features:
 * - Uses 'class' attribute for Tailwind CSS dark mode
 * - Defaults to system theme preference
 * - Enables automatic system theme detection
 * - Persists theme selection in localStorage
 *
 * @example
 * ```tsx
 * <ThemeProvider>
 *   <App />
 * </ThemeProvider>
 * ```
 */
export function ThemeProvider({ children, ...props }: ThemeProviderProps) {
  return (
    <NextThemesProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
      {...props}
    >
      {children}
    </NextThemesProvider>
  )
}
